"use client"

import { NavLink } from "react-router-dom"
import { Button } from "@/components/ui/controls/button"
import { AppLink } from "@/components/navigation/app-link"
import { cn } from "@/lib/utils"

import { SIGMAI_LOGO_SRC } from "@/lib/brand"

const LOGO_SRC = SIGMAI_LOGO_SRC

const navLinkClass =
  "whitespace-nowrap text-[10px] text-white/75 transition-colors hover:text-white sm:text-xs md:text-base"
const navLinkActive = "font-medium text-white"

const navItems: Array<{
  to: string
  label: string
  mobileLabel: string
  end?: boolean
}> = [
  { to: "/", end: true, label: "Home", mobileLabel: "Home" },
  { to: "/platform", label: "App", mobileLabel: "App" },
  { to: "/players", label: "Players", mobileLabel: "Players" },
  { to: "/team", label: "Team", mobileLabel: "Team" },
]

type SiteHeaderProps = {
  onLoginClick?: () => void
}

/**
 * Marketing chrome: frosted dark glass overlay. Page heroes run underneath;
 * they add their own top padding so titles clear the bar.
 */
export function SiteHeader({ onLoginClick }: SiteHeaderProps) {
  return (
    <header className="pointer-events-none fixed inset-x-0 top-0 z-50">
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <div
          className="absolute inset-0 backdrop-blur-xl"
          style={{
            WebkitMaskImage:
              "linear-gradient(to bottom, black 0%, black 40%, transparent 100%)",
            maskImage:
              "linear-gradient(to bottom, black 0%, black 40%, transparent 100%)",
          }}
        />
        <div
          className="absolute inset-0 backdrop-blur-sm"
          style={{
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent 20%, black 55%, black 75%, transparent 100%)",
            maskImage:
              "linear-gradient(to bottom, transparent 20%, black 55%, black 75%, transparent 100%)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/90 via-slate-900/65 to-transparent" />
      </div>

      <div className="container pointer-events-auto relative mx-auto px-3 py-2 sm:px-6 sm:py-3 md:py-4">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <AppLink href="/" className="flex shrink-0 items-center">
            <img
              src={LOGO_SRC}
              alt="SigmAI Sports Management"
              className="h-7 w-auto sm:h-8 md:h-9"
            />
          </AppLink>

          <nav className="flex min-w-0 flex-1 items-center justify-end gap-2 sm:gap-4 md:gap-10">
            <div className="flex min-w-0 items-center gap-2 sm:gap-3 md:gap-10">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(navLinkClass, isActive && navLinkActive)
                  }
                >
                  <span className="md:hidden">{item.mobileLabel}</span>
                  <span className="hidden md:inline">{item.label}</span>
                </NavLink>
              ))}
              <AppLink href="/contact" className={navLinkClass}>
                <span className="md:hidden">Contact</span>
                <span className="hidden md:inline">Get in touch</span>
              </AppLink>
            </div>

            <Button
              size="sm"
              onClick={onLoginClick}
              className="h-7 shrink-0 border-0 bg-white px-2 text-[10px] font-semibold text-navbar shadow-sm hover:bg-white/90 sm:h-8 sm:px-3 sm:text-xs md:h-9 md:px-4 md:text-sm"
            >
              Log in
            </Button>
          </nav>
        </div>
      </div>
    </header>
  )
}
